import React, { useEffect, useState } from "react";

// hooks
import { useRedux } from "../../../hooks/index";
import { createSelector } from "reselect";
// components
import Loader from "../../../components/Loader";
import FormInput from "../../../components/FormInput";
import AppSimpleBar from "../../../components/AppSimpleBar";

import {
  Row,
  Col,
  Tabs,
  Tab,
  Card,
  CardHeader,
  CardBody,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";

import BeatLoader from "react-spinners/BeatLoader";
import { useSnackbar } from "../../../utils/snackbar";

// validations
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";

// actions
import { getProfileDetails } from "../../../redux/actions";
import { registerUserAccount } from "../../../services/auth";

import { createProfile, getProfile } from "../../../api";
import { ProfileTypes } from "../../../data";

import { successNotify, errorNotify } from "../../../utils/toast";
import LanguageSwitcher from "../../../components/LanguageSelector";
import { useTranslation } from "react-i18next";

interface IndexProps {}
const UserInfo = (props: IndexProps) => {
  const { t, i18n } = useTranslation();

  const showSnackbar = useSnackbar();

  const [loading, setLoading] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState(false);
  const [key, setKey] = React.useState<string | null>("user_info");

  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userProfile, setUserProfile] = useState<ProfileTypes | null>();

  const [selectedCountryValue, setSelectedCountryValue] = useState<string>("1");
  const [selectedCurrencyValue, setSelectedCurrencyValue] =
    useState<string>("1");
  const [bankType, setBankType] = useState<string>("1");

  const defaultValues: any = {
    full_name: userProfile?.full_name,
    email: userProfile?.email,
    company_name: userProfile?.company_name,
  };

  const resolver = yupResolver(
    yup.object().shape({
      full_name: yup.string().required("Please enter full name"),
      email: yup
        .string()
        .email("This value should be a valid email.")
        .required("Please enter an email."),
    }),
  );

  const methods = useForm({ defaultValues, resolver });
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = methods;

  // global store
  const { dispatch, useAppSelector } = useRedux();

  const errorData = createSelector(
    (state: any) => state.Profile,
    state => ({
      profileDetails: state.profileDetails,
      getProfileLoading: state.getProfileLoading,
      isProfileFetched: state.isProfileFetched,
    }),
  );

  const { profileDetails, getProfileLoading, isProfileFetched } =
    useAppSelector(errorData);

  useEffect(() => {
    const fetchProfile = async () => {
      const userProfileData = await getProfile();
      if (userProfileData) {
        reset({
          full_name: userProfileData?.data?.full_name,
          email: userProfileData?.data?.email,
          company_name: userProfileData?.data?.company_name,
          apt_suite: userProfileData?.data?.apt_suite,
          postal_code: userProfileData?.data?.postal_code,
          city: userProfileData?.data?.city,
          country: userProfileData?.data?.country,
          vat_id: userProfileData?.data?.vat_id,
          tax_number: userProfileData?.data?.tax_number,
          phone_number: userProfileData?.data?.phone_number,
          eori_number: userProfileData?.data?.eori_number,
          fax_number: userProfileData?.data?.fax_number,
          website: userProfileData?.data?.website,
          account_holder: userProfileData?.data?.account_holder,
          iban: userProfileData?.data?.iban,
          bank: userProfileData?.data?.bank,
          bic: userProfileData?.data?.bic,
          street: userProfileData?.data?.street,
          state: userProfileData?.data?.state,
          paypal_account: userProfileData?.data?.paypal_account,
        });
        setBankType(userProfileData?.payment_type ?? "1");
        setSelectedCountryValue(userProfileData?.country_type);
        setSelectedCurrencyValue(userProfileData?.currency_type);
      }
    };

    fetchProfile();
  }, []);

  const onSubmitForm = async (values: ProfileTypes) => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...values,

        payment_type: bankType,
        country_type: selectedCountryValue,
        currency_type: selectedCurrencyValue,
      };

      setLoading(true);

      const response: any = await createProfile(payload);
      if (response.success == false) {
        showSnackbar(response.message, "error");
        setLoading(false);
      } else {
        showSnackbar(response.message, "info");
        setLoading(false);
      }
    } catch (error: any) {
      showSnackbar(error.message, "error");
      setLoading(false);
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  // Function to toggle visibility
  const handleToggle = () => {
    setIsVisible(!isVisible);
  };

  const handleSelectCountryChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setSelectedCountryValue(event.target.value);
  };
  const handleSelectCurrencyChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setSelectedCurrencyValue(event.target.value);
  };

  const handleSelectBankType = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setBankType(event.target.value);
  };

  return (
    <Form onSubmit={handleSubmit(onSubmitForm)} className="position-relative">
      <Row>
        <Col lg={6}>
          <Card>
            <CardHeader className="card-header">{t("User Info")}</CardHeader>
            <CardBody>
              <div className="">
                <div className="d-flex flex-column">
                  <div className="justify-content-center my-auto row">
                    <div className="">
                      <div className="mb-3">
                        <Row className="align-items-center">
                          <Col lg="4" md="4" className="vertical-center">
                            <span className="default-text">
                              {t("Company Name")}
                            </span>
                          </Col>
                          <Col lg="8" md="8">
                            <FormInput
                              label=""
                              type="text"
                              name="company_name"
                              register={register}
                              errors={errors}
                              control={control}
                              labelClassName="form-label"
                              className="form-control"
                            />
                          </Col>
                        </Row>
                      </div>
                      <div className="mb-3">
                        <Row className="align-items-center">
                          <Col lg="4" md="4" className="vertical-center">
                            <label className="default-text">
                              {t("Full Name")}
                              <span className="required-field">*</span>
                            </label>
                          </Col>
                          <Col lg="8" md="8">
                            <FormInput
                              label=""
                              type="text"
                              name="full_name"
                              register={register}
                              errors={errors}
                              control={control}
                              labelClassName="form-label"
                              className="form-control "
                            />
                          </Col>
                        </Row>
                      </div>
                      <div className="mb-3">
                        <Row className="align-items-center">
                          <Col lg="4" md="4" className="vertical-center">
                            <label className="default-text">
                              {t("E-Mail")}{" "}
                              <span className="required-field">*</span>
                            </label>
                          </Col>
                          <Col lg="8" md="8">
                            <FormInput
                              label=""
                              type="text"
                              name="email"
                              register={register}
                              errors={errors}
                              control={control}
                              labelClassName="form-label"
                              className="form-control "
                            />
                          </Col>
                        </Row>
                      </div>
                      <div className="mb-3">
                        <Row className="align-items-center">
                          <Col lg="4" md="4" className="vertical-center">
                            <label className="default-text">
                              {t("Phone Number")}
                            </label>
                          </Col>
                          <Col lg="8" md="8">
                            <FormInput
                              label=""
                              type="text"
                              name="phone_number"
                              register={register}
                              errors={errors}
                              control={control}
                              labelClassName="form-label"
                              className="form-control "
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="mb-3">
                        <Row className="align-items-center">
                          <Col lg="4" md="4" className="vertical-center">
                            <label className="default-text">{t("Fax")}</label>
                          </Col>
                          <Col lg="8" md="8">
                            <FormInput
                              label=""
                              type="text"
                              name="fax_number"
                              register={register}
                              errors={errors}
                              control={control}
                              labelClassName="form-label"
                              className="form-control "
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="mb-3">
                        <Row className="align-items-center">
                          <Col lg="4" md="4" className="vertical-center">
                            <label className="default-text">
                              {t("Website")}
                            </label>
                          </Col>
                          <Col lg="8" md="8">
                            <FormInput
                              label=""
                              type="text"
                              name="website"
                              register={register}
                              errors={errors}
                              control={control}
                              labelClassName="form-label"
                              placeholder=""
                              className="form-control "
                            />
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="card-header">{t("Bank Info")}</CardHeader>
            <CardBody>
              <Row className="g-2 mb-3">
                <Col md={4} className="vertical-center">
                  <span className="default-text">{t("Payment Type")}</span>
                </Col>
                <Col md="8">
                  <Form.Group controlId="exampleSelect">
                    <Form.Select
                      value={bankType}
                      onChange={handleSelectBankType}
                      aria-label="Select example"
                    >
                      <option value="1">{t("Bank")}</option>
                      <option value="2">{t("Paypal")}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              {bankType == "1" && (
                <>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">
                        {t("Account Holder")}
                      </span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="account_holder"
                        labelClassName="form-label"
                        placeholder=""
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("IBAN")}</span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="iban"
                        labelClassName="form-label"
                        placeholder=""
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("Bank")}</span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="bank"
                        labelClassName="form-label"
                        placeholder=""
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">{t("BIC")}</span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="bic"
                        labelClassName="form-label"
                        placeholder=""
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>
                </>
              )}

              {bankType == "2" && (
                <>
                  <Row className="g-2 mb-3">
                    <Col md={4} className="vertical-center">
                      <span className="default-text">
                        {t("Paypal Account")}
                      </span>
                    </Col>
                    <Col md="8">
                      <FormInput
                        type="text"
                        name="paypal_account"
                        labelClassName="form-label"
                        placeholder=""
                        className="form-control"
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </Col>
                  </Row>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col lg={6}>
          <Card>
            <CardHeader className="card-header">{t("Address")}</CardHeader>
            <CardBody>
              <Row className="g-2 mb-3">
                <Col md={4} className="vertical-center">
                  <span className="default-text">{t("Billing Street")}</span>
                </Col>
                <Col md="8">
                  <FormInput
                    type="text"
                    name="street"
                    labelClassName="form-label"
                    placeholder=""
                    className="form-control"
                    register={register}
                    errors={errors}
                    control={control}
                  />
                </Col>
              </Row>
              <Row className="g-2 mb-3">
                <Col md={4} className="vertical-center">
                  <span className="default-text">{t("Apt/Suite")}</span>
                </Col>
                <Col md="8">
                  <FormInput
                    type="text"
                    name="apt_suite"
                    labelClassName="form-label"
                    placeholder=""
                    className="form-control"
                    register={register}
                    errors={errors}
                    control={control}
                  />
                </Col>
              </Row>

              <Row className="g-2 mb-3">
                <Col md={4} className="vertical-center">
                  <span className="default-text">{t("Postal Code")}</span>
                </Col>
                <Col md="8">
                  <FormInput
                    type="text"
                    name="postal_code"
                    labelClassName="form-label"
                    placeholder=""
                    className="form-control"
                    register={register}
                    errors={errors}
                    control={control}
                  />
                </Col>
              </Row>

              <Row className="g-2 mb-3">
                <Col md={4} className="vertical-center">
                  <span className="default-text">{t("City")}</span>
                </Col>
                <Col md="8">
                  <FormInput
                    type="text"
                    name="city"
                    labelClassName="form-label"
                    placeholder=""
                    className="form-control"
                    register={register}
                    errors={errors}
                    control={control}
                  />
                </Col>
              </Row>
              <Row className="g-2 mb-3">
                <Col md={4} className="vertical-center">
                  <span className="default-text">{t("State/Province")}</span>
                </Col>
                <Col md="8">
                  <FormInput
                    type="text"
                    name="state"
                    labelClassName="form-label"
                    placeholder=""
                    className="form-control"
                    register={register}
                    errors={errors}
                    control={control}
                  />
                </Col>
              </Row>

              <Row className="g-2 mb-3">
                <Col md={4} className="vertical-center">
                  <span className="default-text">{t("Country")}</span>
                </Col>
                <Col md="8">
                  <Form.Group controlId="country">
                    <Form.Select
                      value={selectedCountryValue}
                      onChange={handleSelectCountryChange}
                      aria-label="Select example"
                    >
                      <option value="1">{t("Germany")}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </CardBody>
          </Card>
          <Card>
            <CardHeader className="card-header">
              {t("Additional Info")}
            </CardHeader>
            <CardBody>
              <Row className="g-2 mb-3">
                <Col md={4} className="vertical-center">
                  <span className="default-text">{t("VAT ID")}</span>
                </Col>
                <Col md="8">
                  <FormInput
                    type="text"
                    name="vat_id"
                    labelClassName="form-label"
                    placeholder="DE12345"
                    className="form-control"
                    register={register}
                    errors={errors}
                    control={control}
                  />
                </Col>
              </Row>
              <Row className="g-2 mb-3">
                <Col md={4} className="vertical-center">
                  <span className="default-text">{t("Tax Number")}</span>
                </Col>
                <Col md="8">
                  <FormInput
                    type="text"
                    name="eori_number"
                    labelClassName="form-label"
                    placeholder="DE12345"
                    className="form-control"
                    register={register}
                    errors={errors}
                    control={control}
                  />
                </Col>
              </Row>
              <Row className="g-2 mb-3">
                <Col md={4} className="vertical-center">
                  <span className="default-text">{t("EORI Number")}</span>
                </Col>
                <Col md="8">
                  <FormInput
                    type="text"
                    name="eori_number"
                    labelClassName="form-label"
                    placeholder="DE12345"
                    className="form-control"
                    register={register}
                    errors={errors}
                    control={control}
                  />
                </Col>
              </Row>
              <Row className="g-2 mb-3">
                <Col md={4} className="vertical-center">
                  <span className="default-text">{t("Currency")}</span>
                </Col>
                <Col md="8">
                  <Form.Group controlId="">
                    <Form.Select
                      value={selectedCurrencyValue}
                      onChange={handleSelectCurrencyChange}
                      aria-label=""
                    >
                      <option value="1">Euro (EUR) €</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <div className="d-flex justify-content-end">
            <Button
              variant="primary"
              onClick={handleSubmit(onSubmitForm)}
              disabled={loading}
            >
              {t("Save")}
              {loading && (
                <Spinner animation="border" size="sm" className="ms-2" />
              )}
            </Button>
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default UserInfo;
